import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { AddCircle, Delete, Edit, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Card, Divider, IconButton, ListItem, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, NoteModal } from '/src/components';
import { useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { toastMessages } from '/src/lib/messages';
import { Note, TNoteId } from '/src/lib/models';
import { CRUDMode, TCreateNoteRequest, TUpdateNoteRequest, UserRole } from '/src/lib/types';

export type NotesProps = {
  /**
   * CRUD operation to perform
   */
  mode: CRUDMode;
  /**
   * List of Notes to display
   */
  notes: Note[];
  /**
   * This component's index in a page sidebar
   */
  sidebarIndex: number;
  /**
   * The other page sidebar items
   */
  sidebarItems: boolean[];
  /**
   * Sets the opened/closed status of page sidebar items
   * @param items Sidebar items
   * @returns void
   */
  setSidebarItems: (items: boolean[]) => void;
  /**
   * Saves the updated notes to the database
   * @param notes Updated notes
   * @returns void | Promise<void>
   */
  updateNotes?: (notes: Note[]) => void | Promise<void>;
  /**
   * Component title. Displayed in the header
   */
  title?: string;
  /**
   * Tooltip for Add Note button
   */
  tooltip?: string;
};

export const Notes: React.FC<NotesProps> = observer(
  ({
    mode,
    notes,
    updateNotes,
    sidebarIndex,
    sidebarItems,
    setSidebarItems,
    title = 'Notes',
    tooltip = 'Add Note',
  }) => {
    /** Context **/
    const userStore = useUserStore();

    /** State **/
    const [selectedNoteId, setSelectedNoteId] = useState<TNoteId>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    /** Computed **/
    const selectedNote = notes.find((note) => note._id === selectedNoteId);

    /** Methods **/

    /**
     * Return a Request object with `user` and `roles` pre-populated
     */
    const getNoteUser = (): TCreateNoteRequest | TUpdateNoteRequest => {
      const noteData: TCreateNoteRequest | TUpdateNoteRequest = {
        user: {
          id: userStore.user?._id,
          displayName: userStore.user?.displayName,
        },
        roles: [userStore.isAdmin ? UserRole.Admin : UserRole.Company],
      };

      return noteData;
    };

    /**
     * Show a Create Note dialog
     * @param noteId TNoteId
     */
    const confirmCreate = () => {
      setShowDeleteModal(false);
      setShowEditModal(false);
      setShowCreateModal(true);
    };

    /**
     * Show an Edit Note dialog
     * @param noteId TNoteId
     */
    const confirmEdit = (noteId: TNoteId) => {
      setSelectedNoteId(noteId);
      setShowDeleteModal(false);
      setShowCreateModal(false);
      setShowEditModal(true);
    };

    /**
     * Show a delete confirmation dialog
     * @param noteId TNoteId
     */
    const confirmDelete = (noteId: TNoteId) => {
      setSelectedNoteId(noteId);
      setShowEditModal(false);
      setShowCreateModal(false);
      setShowDeleteModal(true);
    };

    /**
     * Create a new Note
     */
    const createNote = async (content = '') => {
      if (!updateNotes) return;

      try {
        const newNote = {
          ...(getNoteUser() as TCreateNoteRequest),
          content,
          created: new Date(),
          updated: new Date(),
        } as Note;

        await updateNotes([...notes, newNote]);

        // Show alert
        toast.success(toastMessages.createNote.success);

        // Open notes list item (if applicable)
        const items = sidebarItems;
        items[sidebarIndex] = true;
        setSidebarItems(items);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.createNote.error);
      }
    };

    /**
     * Update an existing Note
     */
    const editNote = async (noteId: TNoteId, content = '') => {
      if (!updateNotes) return;

      try {
        // Get Note index
        const noteIndex = notes.findIndex((note) => note._id === noteId);
        if (noteIndex === -1) throw new Error(`No Note with ID "${noteId}" found in Notes list`);

        const note = {
          ...notes[noteIndex],
          ...(getNoteUser() as TUpdateNoteRequest),
          content,
          updated: new Date(),
        } as Note;

        const updatedNotes = notes;
        updatedNotes[noteIndex] = note;

        // Push updates to the database
        await updateNotes(updatedNotes);

        // Show alert
        toast.success(toastMessages.updateNote.success);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.updateNote.error);
      }
    };

    /**
     * Remove a Note
     */
    const removeNote = async (noteId: TNoteId) => {
      if (!updateNotes) return;

      try {
        // Get Note index
        const noteIndex = notes.findIndex((note) => note._id === noteId);
        if (noteIndex === -1) throw new Error(`No Note with ID "${noteId}" found in Notes list`);

        const updatedNotes = notes;
        updatedNotes.splice(noteIndex, 1);

        // Push updates to the database
        await updateNotes(updatedNotes);

        if (selectedNoteId === noteId) setSelectedNoteId(undefined);

        // Show alert
        toast.success(toastMessages.deleteNote.success);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.deleteNote.error);
      }
    };

    /** Render **/
    return (
      <>
        <Card component="md-card" className="no-margin-bottom md-default-theme text-center">
          <ListItem className="toolbar-div pad-left">
            <Tooltip title={tooltip}>
              <IconButton className="md-icon-button float-left" onClick={() => confirmCreate()}>
                <AddCircle style={{ color: 'white' }} className="md-icon material-icons" />
              </IconButton>
            </Tooltip>

            <Box sx={{ flex: 1 }}>{title}</Box>
            <Box display="flex" justifyContent="end" flex={1}>
              <Box component="span" className="float-right">
                <IconButton
                  className="md-icon-button"
                  onClick={() => setSidebarItems(sidebarItems.with(sidebarIndex, !sidebarItems[sidebarIndex]))}
                  disabled={!notes.length}
                >
                  <Tooltip title={sidebarItems[sidebarIndex] ? 'Show Less' : 'Show More'}>
                    {sidebarItems[sidebarIndex] ? (
                      <ExpandLess
                        style={{ color: !notes.length ? undefined : 'white' }}
                        className="md-icon material-icons"
                      />
                    ) : (
                      <ExpandMore
                        style={{ color: !notes.length ? undefined : 'white' }}
                        className="md-icon material-icons"
                      />
                    )}
                  </Tooltip>
                </IconButton>
              </Box>
            </Box>
          </ListItem>
          {sidebarItems[sidebarIndex] &&
            !!notes.length &&
            notes.map((note) => (
              <Box key={note._id} bgcolor="white">
                {/* STANDARD NOTES */}
                <ListItem className="md-2-line">
                  {/* CONTENT */}
                  <Box style={{ textAlign: 'left', padding: 'none', flex: 1 }}>
                    <Box className="md-caption">
                      Added {dayjs(note.created).format(mediumDate)} by {note.user.displayName}
                    </Box>
                    <p className="md-body-2">{note.content}</p>
                  </Box>

                  {/* ACTION BUTTONS */}
                  {mode !== 'read' && (
                    <IconButton className="md-icon-button" onClick={() => confirmEdit(note._id)}>
                      <Edit sx={{ height: '18px', width: '18px' }} />
                    </IconButton>
                  )}
                  {mode !== 'read' && (
                    <IconButton className="md-icon-button md-warn" onClick={() => confirmDelete(note._id)}>
                      <Delete className="md-icon material-icons" sx={{ height: '18px', width: '18px' }} />
                    </IconButton>
                  )}
                </ListItem>

                <Divider />
              </Box>
            ))}
        </Card>

        <NoteModal
          mode={CRUDMode.Create}
          content=""
          open={showCreateModal}
          setOpen={setShowCreateModal}
          onConfirm={(content) => createNote(content)}
        />

        {selectedNoteId && (
          <NoteModal
            mode={CRUDMode.Edit}
            content={selectedNote?.content}
            open={showEditModal}
            setOpen={setShowEditModal}
            onConfirm={(content) => selectedNoteId && editNote(selectedNoteId, content)}
            onCancel={() => setSelectedNoteId(undefined)}
          />
        )}

        <ConfirmModal
          content="Are you sure you want to delete this Note?"
          label="Confirm Deletion"
          open={!!selectedNoteId && showDeleteModal}
          setOpen={setShowDeleteModal}
          okLabel="Delete"
          cancelLabel="Cancel"
          onOk={() => selectedNoteId && removeNote(selectedNoteId)}
          onCancel={() => setSelectedNoteId(undefined)}
        />
      </>
    );
  }
);
